/* Moving shares from one of your accounts to another.
 *
 * A panel on the lot, not a screen. `deriveBook` groups lots by ticker, so a
 * transfer changes no weight, no market value, no theme, no cash and no book
 * total — a before-and-after screen in `ReduceClose`'s shape would be four rows
 * printing the same number twice. What it changes is which account holds the
 * shares, which is a fact about the lot and belongs on the lot.
 *
 * It sits beside "Correct this lot" and is deliberately not part of it. That
 * form's own note says it "records no trade and no gain", which is right for a
 * lot filed against the wrong broker and wrong for shares that moved on a date
 * and cost something to move.
 *
 * The button says "Record the move" rather than "Save the correction": the
 * second is the restatement voice and this is not a restatement. There is no
 * "this only writes it down" footer — that line lives on two full screens and
 * on no panel in this app.
 *
 * Everything it refuses and everything it flags is decided in
 * `state/transferShares.ts`; this only says it in sentences. */

import { useState } from 'react';
import { useStore } from '../state/store';
import { accountName } from '../lib/accounts';
import { moneyExact, shares as fmtShares, sharesOf, todayIso } from '../lib/format';
import { decimalOr } from '../lib/number';
import { refuseReason, type TransferRefusal } from '../state/transferShares';
import type { Holding } from '../types';

const WHY: Record<TransferRefusal, string> = {
  'nothing-to-move': 'Enter how many shares moved.',
  'same-account': 'They are already in that account.',
  'no-lot': 'This account does not hold them.',
  'not-enough-shares': 'That is more than this account holds.',
  'futures-destination': 'A futures wallet holds contracts, not shares.',
  'currency-mismatch': 'That account holds this company in another currency. '
    + 'Averaging two currencies into one cost would produce a number that is not one.',
};

export function MoveShares({ holding, say }: { holding: Holding; say: (m: string) => void }) {
  const { state, transferShares } = useStore();
  const accounts = state.data.accounts;

  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [count, setCount] = useState('');
  const [at, setAt] = useState(todayIso());
  const [fee, setFee] = useState('');
  const [feeAccount, setFeeAccount] = useState(holding.account);
  const [alreadyMoved, setAlreadyMoved] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setOpen(true)}>
        Move these shares to another account
      </button>
    );
  }

  const moving = decimalOr(count);
  const req = {
    ticker: holding.ticker,
    fromAccount: holding.account,
    toAccount: to,
    shares: moving,
    at: `${at}T12:00:00.000Z`,
    ...(decimalOr(fee) > 0 ? { fee: decimalOr(fee), feeAccount } : {}),
    ...(alreadyMoved ? { alreadyMoved: true } : {}),
  };
  const refusal = to === '' ? null : refuseReason(state.data, accounts, req);
  const ready = to !== '' && moving > 0 && !refusal;

  const dest = to === '' ? null
    : state.data.holdings.find((h) => h.ticker === holding.ticker && h.account === to);
  const shelter = (id: string) => accounts.find((a) => a.id === id)?.taxShielded === true;
  const leavingShelter = to !== '' && shelter(holding.account) && !shelter(to);
  const enteringShelter = to !== '' && !shelter(holding.account) && shelter(to);
  const costUnknown = holding.avgCost <= 0 || (dest ? dest.avgCost <= 0 : false);

  const save = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const out = await transferShares(req);
      if (!out) { say('That move could not be recorded.'); return; }
      say(`${sharesOf(out.record.shares, holding.ticker)} recorded as moved to `
        + `${accountName(accounts, to)}.`);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--edge)' }}>
      <div className="fin-title">Moving these shares</div>

      <div className="field" style={{ marginTop: 10 }}>
        <label htmlFor={`mv-to-${holding.id}`}>Which account they went to</label>
        <select id={`mv-to-${holding.id}`} className="select" value={to}
                onChange={(e) => setTo(e.target.value)}>
          <option value="">Choose…</option>
          {accounts.filter((a) => a.id !== holding.account && !a.isFutures)
            .map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor={`mv-n-${holding.id}`}>How many shares</label>
        <input id={`mv-n-${holding.id}`} className="input" data-mono="true" inputMode="decimal"
               value={count} placeholder={String(holding.shares)}
               onChange={(e) => setCount(e.target.value)} />
        <div className="hint">
          This account holds {fmtShares(holding.shares)}.{' '}
          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }}
                  onClick={() => setCount(String(holding.shares))}>All of them</button>
        </div>
      </div>

      <div className="field">
        <label htmlFor={`mv-at-${holding.id}`}>When</label>
        <input id={`mv-at-${holding.id}`} className="input" type="date"
               value={at} onChange={(e) => setAt(e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor={`mv-fee-${holding.id}`}>Transfer fee</label>
          <input id={`mv-fee-${holding.id}`} className="input" data-mono="true" inputMode="decimal"
                 value={fee} placeholder="0.00" onChange={(e) => setFee(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor={`mv-fa-${holding.id}`}>Charged to</label>
          <select id={`mv-fa-${holding.id}`} className="select" value={feeAccount}
                  onChange={(e) => setFeeAccount(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>
      <div className="hint" style={{ marginTop: -6 }}>
        It comes out of that account&rsquo;s cash and nowhere else. These shares were already
        bought, so what it costs to move them is not part of what they cost.
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <label htmlFor={`mv-done-${holding.id}`}
               style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input id={`mv-done-${holding.id}`} type="checkbox" checked={alreadyMoved}
                 onChange={(e) => setAlreadyMoved(e.target.checked)} />
          <span>The book already shows them in the other account</span>
        </label>
        <div className="hint">
          {alreadyMoved
            ? 'The record is written and no shares are moved. Leave this on if you already changed the account on the lot by hand.'
            : `${moving > 0 ? fmtShares(moving) : 'The shares'} will be taken off this lot when you record it.`}
        </div>
      </div>

      {refusal ? <div className="note" data-tone="crit">{WHY[refusal]}</div> : null}

      {!refusal && costUnknown && to !== '' ? (
        <div className="note" data-tone="warn" style={{ marginTop: 8 }}>
          {holding.avgCost <= 0
            ? 'This lot has no cost recorded, so none travels with the shares.'
            : `${accountName(accounts, to)} holds this company with no cost recorded, so the `
              + 'merged lot loses the cost this one had.'}
          {' '}The average stays unknown rather than becoming a number nobody measured.
        </div>
      ) : null}

      {!refusal && leavingShelter ? (
        <div className="note" data-tone="warn" style={{ marginTop: 8 }}>
          These shares are leaving a sheltered account. This book applies the shelter to the
          account a sale happens in, so every gain on a later sale of them lands in the taxable
          totals here, including the part that built up while they were sheltered. What your tax
          office makes of a transfer in kind is not something this app knows.
        </div>
      ) : null}
      {!refusal && enteringShelter ? (
        <div className="note" style={{ marginTop: 8 }}>
          These shares are entering a sheltered account, so later sales of them are reported here
          outside the tax base. No gain, tax or new cost is worked out today.
        </div>
      ) : null}

      {!refusal && decimalOr(fee) > 0 ? (
        <div className="hint" style={{ marginTop: 8 }}>
          {moneyExact(decimalOr(fee), (state.data.cash.find((c) => c.account === feeAccount)?.currency ?? 'USD') as 'USD')}
          {' '}off {accountName(accounts, feeAccount)}. Nothing else moves: no gain, no money paid
          in, and neither average.
        </div>
      ) : null}

      <div className="fab-row" style={{ marginTop: 12 }}>
        <button className="btn btn-block" onClick={() => setOpen(false)}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!ready || busy}
                onClick={() => void save()}>
          Record the move
        </button>
      </div>
    </div>
  );
}
